import React from 'react';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import PrecisionImage from '@/asset/unnamed.jpg';
import Card from '../ui/Card';

export const WhyInverikaSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const pillars = [
    {
      icon: 'precision_manufacturing',
      title: 'Technical Precision',
      desc: 'Engineered accuracy in every measurement, ensuring facade stability and structural longevity.',
    },
    {
      icon: 'integration_instructions',
      title: 'Integrated Execution',
      desc: 'Seamless management of multi-disciplinary teams from fabrication to final installation.',
    },
    {
      icon: 'health_and_safety',
      title: 'Zero-Harm Safety',
      desc: 'Rigorous institutional safety protocols that protect manpower and project assets.',
    },
    {
      icon: 'hub',
      title: 'Stakeholder Coordination',
      desc: 'Transparent reporting and proactive communication with architects and developers.',
    },
  ];

  const metrics = [
    { value: '120+', label: 'PROJECTS DELIVERED' },
    { value: '0', label: 'SAFETY INCIDENTS' },
    { value: '98%', label: 'CLIENT RETENTION' },
    { value: '25+', label: 'CITY FOOTPRINT' },
  ];

  return (
    <Box
      id="why-us"
      sx={{
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* ========================================================= */}
      {/* PART 1: Why Industry Leaders Trust Inverika & Metrics   */}
      {/* ========================================================= */}
      <Box sx={{ py: { xs: 10, md: 12 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Left Column: Heading, Intro & 2 Feature Highlights */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ pr: { lg: 4 } }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 1,
                  }}
                >
                  WHY INDUSTRY LEADERS
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2.25rem', md: '3rem' },
                    color: theme.palette.text.primary,
                    fontFamily: 'Space Grotesk',
                    fontWeight: 700,
                    mb: 3,
                    letterSpacing: '-0.01em',
                  }}
                >
                  TRUST INVERIKA
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '1.05rem',
                    lineHeight: 1.6,
                    mb: 5,
                    maxWidth: 580,
                  }}
                >
                  We go beyond traditional contracting. Our approach combines rigorous engineering discipline with a deep understanding of architectural intent and stakeholder coordination.
                </Typography>

                {/* Feature List */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Feature 1 */}
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: '#ffffff',
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 24 }}>
                        verified
                      </span>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: theme.palette.text.primary,
                          textTransform: 'uppercase',
                          mb: 0.5,
                          fontSize: '0.9375rem',
                        }}
                      >
                        EXECUTION DISCIPLINE
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                          maxWidth: 480,
                        }}
                      >
                        Adherence to project timelines through meticulous resource planning and site-readiness protocols.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Feature 2 */}
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: '#ffffff',
                      }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 24 }}>
                        handshake
                      </span>
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: theme.palette.text.primary,
                          textTransform: 'uppercase',
                          mb: 0.5,
                          fontSize: '0.9375rem',
                        }}
                      >
                        STAKEHOLDER COORDINATION
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                          maxWidth: 480,
                        }}
                      >
                        Seamless interface between architects, MEP consultants, and main contractors for conflict-free delivery.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Column: 2x2 Metric Cards Grid */}
            <Grid item xs={12} lg={6}>
              <Grid container spacing={3}>
                {metrics.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        p: 4,
                        minHeight: 180,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: isDark ? 'none' : '0 8px 30px rgba(0, 0, 0, 0.04)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{
                          color: theme.palette.primary.main,
                          fontFamily: 'Space Grotesk',
                          fontWeight: 700,
                          fontSize: { xs: '2.5rem', md: '3.25rem' },
                          mb: 1,
                          lineHeight: 1,
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ========================================================= */}
      {/* PART 2: Built on Institutional Precision (Original Pillars) */}
      {/* ========================================================= */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Left Column: 4 Value Pillars */}
            <Grid item xs={12} lg={6}>
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2rem', md: '3rem' },
                    color: theme.palette.text.primary,
                    fontFamily: 'Space Grotesk',
                    fontWeight: 700,
                    mb: 6,
                  }}
                >
                  Built on Institutional{' '}
                  <Box component="span" sx={{ color: theme.palette.primary.main }}>
                    Precision.
                  </Box>
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {pillars.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1,
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.primary.main}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{ fontSize: 24, color: theme.palette.primary.main }}
                        >
                          {item.icon}
                        </span>
                      </Box>

                      <Box>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 600, mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Right Column: Visual Card with Precision Image & 100% Accuracy Target Badge */}
            <Grid item xs={12} lg={6}>
              <Box sx={{ position: 'relative' }}>
                <Card glass sx={{ p: 1, border: `1px solid ${theme.palette.divider}`, height: 440, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 1,
                      backgroundImage: `url(${PrecisionImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'left center',
                      opacity: 0.9,
                    }}
                  />
                </Card>

                {/* Floating 100% Accuracy Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -24,
                    right: -24,
                    backgroundColor: theme.palette.primary.main,
                    p: 4,
                    borderRadius: 2,
                    boxShadow: '0 10px 40px rgba(0, 102, 255, 0.4)',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  <Typography variant="h3" sx={{ color: '#ffffff', fontFamily: 'Space Grotesk', fontWeight: 700, lineHeight: 1 }}>
                    100%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, mt: 1, display: 'block' }}>
                    Accuracy Target
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default WhyInverikaSection;
